import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ROUTE_META, canonicalFor, OG_IMAGE, type RouteMeta } from '@/lib/seo/routeMeta';

interface SeoHeadProps {
  /** Public route path, e.g. "/pricing". Must exist in ROUTE_META. */
  route: string;
  /** Optional per-render overrides (e.g. a compare preset name in the title). */
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  /** Extra head tags (JSON-LD, etc). */
  children?: React.ReactNode;
}

/**
 * Renders the per-route head tags from the shared ROUTE_META source, so the
 * runtime tags and the build-time baked HTML (scripts/bakeRouteHtml.ts) can't drift.
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  route,
  title,
  description,
  ogTitle,
  ogDescription,
  children,
}) => {
  const base: RouteMeta = ROUTE_META[route] ?? { title: 'LED Logic', description: '' };
  const finalTitle = title ?? base.title;
  const finalDescription = description ?? base.description;
  const finalOgTitle = ogTitle ?? base.ogTitle ?? finalTitle;
  const finalOgDescription = ogDescription ?? base.ogDescription ?? finalDescription;
  const url = canonicalFor(route);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {children}
    </Helmet>
  );
};

export default SeoHead;
